/**
 * API MyProfilePort implementation.
 *
 * Replaces fixtureMyProfilePort with real backend API adapter.
 *
 * ACT-INDEEP-FRONTEND-MY-PROFILE-API-ADAPTER01
 *
 * S018 authority: Figma node 39:4695
 *
 * Endpoint selection:
 * - registrationKind === 'sponsor' -> GET /api/v1/sponsor/profile
 * - registrationKind === 'friend' -> GET /api/v1/friend/profile
 * - Unknown/unclassified/contradictory -> fail closed
 *
 * Field derivation:
 * - accountId: from authStore (hydrateFromSession sets account_id)
 * - displayName: from response display_name (required, non-empty after trim)
 * - age: from response age (0 = not set, use 0)
 * - city: from response city_or_region (required, non-empty after trim)
 * - avatarUrl: always empty string (no avatar in backend)
 * - completionPercent: from response completion_percent if available
 *   Falls back to PARTIAL if not in contract (ACT-INDEEP-FRONTEND-MY-PROFILE-API-ADAPTER01)
 *
 * Security posture:
 * - accountId is operational data, never rendered or logged
 * - No raw response body or error details exposed
 * - AbortSignal properly forwarded
 * - Fail-closed for unknown/unsupported registration kinds
 * - Strict runtime validation - reject malformed DTOs
 */
import type { MyProfilePort, MyProfileSummary } from '../port';
import { useAuthStore } from '@/store/authStore';

const API_BASE = '/api/v1';

/**
 * Backend response DTO per backend handlers and OpenAPI schema.
 * 
 * Contract authority:
 * - Backend: internal/profiles/sponsor_profile_handlers.go:16-30 (SponsorProfileResponse)
 * - Backend: internal/profiles/friend_profile_handlers.go:21-35 (ProfileResponse)
 * - OpenAPI: api/openapi/schemas/profiles.yaml:30-113 (FriendProfile)
 * - OpenAPI: api/openapi/schemas/profiles.yaml:114-194 (SponsorProfile)
 * 
 * All fields are optional per backend with omitempty:
 * - display_name: string (omitempty) - pseudonymous display name
 * - age: int (omitempty) - 0 means not set per backend comment
 * - city_or_region: string (omitempty) - geographic hint
 * - completion_percent: NOT in backend contract - successor ACT required
 */
interface ProfileResponseDTO {
  display_name?: string;
  age?: number;
  city_or_region?: string;
  completion_percent?: never; // Not in backend contract - reserved for future
}

/**
 * Validate required string field - must be non-empty after trim.
 * Returns null if validation fails (fail-closed).
 */
function validateRequiredString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  return trimmed;
}

/**
 * Validate age field - must be non-negative integer.
 * Negative, fractional, NaN, or Infinity values are rejected.
 * Zero is valid (not set state).
 */
function validateAge(value: unknown): number | null {
  if (typeof value !== 'number') {
    return null;
  }
  if (!Number.isInteger(value) || !Number.isFinite(value)) {
    return null;
  }
  if (value < 0) {
    return null;
  }
  return value;
}

/**
 * Validate and normalize profile data into MyProfileSummary.
 * Returns null if validation fails (fail-closed).
 *
 * Strict validation rules:
 * - display_name: required, non-empty string after trim
 * - age: required, non-negative integer (0 = not set)
 * - city_or_region: required, non-empty string after trim
 * - completion_percent: optional number 0-100
 */
function validateAndNormalize(
  data: unknown,
  accountId: string
): MyProfileSummary | null {
  // Validate input is object
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    return null;
  }

  const profile = data as Record<string, unknown>;

  // Validate accountId - must match authenticated authority
  // Response account_id is IGNORED to prevent identity substitution attacks
  const authenticatedAccountId = accountId.trim();
  if (!authenticatedAccountId) {
    return null;
  }
  // Reject if response tries to substitute a different account_id
  if (
    profile.account_id !== undefined &&
    profile.account_id !== authenticatedAccountId
  ) {
    return null;
  }

  // Validate display_name - required, non-empty after trim
  const displayName = validateRequiredString(profile.display_name);
  if (displayName === null) {
    return null;
  }

  // Validate profile id (canonical UUID returned by the backend).
  // CORRECTION01 (R1 fix): the ProfileDetail page wrapper uses this
  // id to compute `isOwnProfile`. Without a valid profile id, the
  // page wrapper cannot determine whether the viewed profile is the
  // viewer's own profile, and the CTA is hidden unconditionally.
  // Fail closed if the backend omits the id.
  const rawProfileId = profile.id;
  if (typeof rawProfileId !== 'string' || rawProfileId.trim().length === 0) {
    return null;
  }
  const profileId = rawProfileId.trim();

  // Validate age - required, non-negative integer
  const age = validateAge(profile.age);
  if (age === null) {
    return null;
  }

  // Validate city_or_region - required, non-empty after trim
  const city = validateRequiredString(profile.city_or_region);
  if (city === null) {
    return null;
  }

  // AvatarUrl is always empty - backend does not provide avatar URLs
  const avatarUrl = '';

  // Completion percent - PARTIAL: Not verified against real backend/OpenAPI contract
  // If present, must be valid integer 0-100; malformed values fail closed
  let completionPercent: number | undefined;
  if (profile.completion_percent !== undefined) {
    const cp = profile.completion_percent;
    if (
      typeof cp !== 'number' ||
      !Number.isInteger(cp) ||
      !Number.isFinite(cp) ||
      cp < 0 ||
      cp > 100
    ) {
      return null; // Contract violation - fail closed
    }
    completionPercent = cp;
  }

  // Return frozen immutable summary
  // SECURITY: accountId always comes from authenticated store
  return Object.freeze({
    accountId: authenticatedAccountId,
    profileId,
    displayName,
    age,
    city,
    avatarUrl,
    completionPercent,
  });
}

/**
 * Generic fetch helper for profile endpoints.
 * Properly handles AbortError for cancellation.
 *
 * @throws DOMException with name 'AbortError' when signal is aborted
 * @throws Error with generic message for other failures
 */
async function fetchProfile<T>(
  url: string,
  signal: AbortSignal
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      signal,
    });
  } catch (error) {
    // Check abort - if aborted, throw the actual abort reason
    if (signal.aborted) {
      signal.throwIfAborted();
    }
    // Preserve AbortError from other sources
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    // Network errors (DNS, connection refused, etc.) are normalized
    // to generic error to avoid leaking error details
    throw new Error('profile load failed');
  }

  // Handle HTTP errors
  if (!response.ok) {
    // 401/403/404/5xx all become generic profile load failure
    // No endpoint, account, or token details exposed
    throw new Error('profile load failed');
  }

  // Parse JSON - AbortError can occur during body consumption
  // e.g., signal aborted after headers received but before body fully read
  let data: unknown;
  try {
    data = await response.json();
  } catch (error) {
    // Check abort AFTER json() rejects - if aborted, throw the actual abort reason
    if (signal.aborted) {
      signal.throwIfAborted();
    }
    // Also check for AbortError from other sources
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    // Other JSON parse errors (malformed JSON, etc.) normalized
    throw new Error('profile load failed');
  }

  // Validate response structure
  if (data === null || typeof data !== 'object') {
    throw new Error('profile load failed');
  }

  return data as T;
}

/**
 * Create an API-based MyProfilePort.
 *
 * Selects the appropriate endpoint based on registrationKind:
 * - 'sponsor' -> /api/v1/sponsor/profile
 * - 'friend' -> /api/v1/friend/profile
 * - other -> fail closed
 *
 * @param getRegistrationKind - Function to get current registration kind
 * @param getAccountId - Function to get current account ID
 */
export function createApiMyProfilePort(
  getRegistrationKind: () => ReturnType<typeof useAuthStore.getState>['registrationKind'],
  getAccountId: () => ReturnType<typeof useAuthStore.getState>['accountId']
): MyProfilePort {
  return {
    async loadMyProfile(signal: AbortSignal): Promise<MyProfileSummary> {
      // Get registration kind and account ID from auth store
      const registrationKind = getRegistrationKind();
      const accountId = getAccountId();

      // Fail closed for unsupported/unknown registration kinds
      if (registrationKind !== 'sponsor' && registrationKind !== 'friend') {
        throw new Error('profile load failed');
      }

      // Fail closed if accountId not available
      if (!accountId) {
        throw new Error('profile load failed');
      }

      // Select endpoint based on registration kind
      const endpoint = registrationKind === 'sponsor'
        ? `${API_BASE}/sponsor/profile`
        : `${API_BASE}/friend/profile`;

      // Fetch profile with strict DTO validation
      const data = await fetchProfile<ProfileResponseDTO>(endpoint, signal);

      // Validate and normalize response (fail-closed)
      const summary = validateAndNormalize(data, accountId);

      if (!summary) {
        throw new Error('profile load failed');
      }

      return summary;
    },
  };
}

/**
 * Create the production API-based MyProfilePort.
 * Uses the actual auth store for registrationKind and accountId.
 *
 * SECURITY: accountId is operational data, never rendered or logged.
 */
export function createProductionApiPort(): MyProfilePort {
  return createApiMyProfilePort(
    () => useAuthStore.getState().registrationKind,
    () => useAuthStore.getState().accountId
  );
}
